import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect } from 'react';
import { queryKeys } from '@/lib/queryKeys';
import { onAuthStateChange } from '@/lib/supabaseAdapter';
import {
  getCurrentUserProfile,
  hasVenueAccess,
  type VenueProfile,
} from '@/services/venueProfileService';

interface VenueProfileData {
  profile: VenueProfile | null;
  isOwner: boolean;
}

interface VenueProfileContextType {
  profile: VenueProfile | null;
  isLoading: boolean;
  isError: boolean;
  isOwner: boolean;
  refreshProfile: () => Promise<void>;
}

const VenueProfileContext = createContext<VenueProfileContextType | undefined>(undefined);

export function VenueProfileProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.venueProfile.current,
    queryFn: async (): Promise<VenueProfileData> => {
      const [profile, { isOwner }] = await Promise.all([getCurrentUserProfile(), hasVenueAccess()]);
      return { profile, isOwner };
    },
  });

  const refreshProfile = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.venueProfile.all,
    });
  }, [queryClient]);

  useEffect(() => {
    const {
      data: { subscription },
    } = onAuthStateChange(async (event, _session: Session | null) => {
      if (event === 'SIGNED_IN') {
        queryClient.invalidateQueries({ queryKey: queryKeys.venueProfile.all });
      } else if (event === 'SIGNED_OUT') {
        queryClient.setQueryData(queryKeys.venueProfile.current, {
          profile: null,
          isOwner: false,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  return (
    <VenueProfileContext.Provider
      value={{
        profile: data?.profile ?? null,
        isLoading,
        isError,
        isOwner: data?.isOwner ?? false,
        refreshProfile,
      }}
    >
      {children}
    </VenueProfileContext.Provider>
  );
}

export function useVenueProfile() {
  const context = useContext(VenueProfileContext);
  if (context === undefined) {
    throw new Error('useVenueProfile must be used within a VenueProfileProvider');
  }
  return context;
}
